// src/components/AliarseModal.js
// Wizard modal para el flujo "Aliarse a la red" (aliado comercial bajo FENAGRO).
// Alcance Fase B: datos básicos + envío a backend. Documentos y pago Wompi
// quedan pendientes para fases posteriores (ver docs/ROADMAP_ALIADOS.md).

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, TextField, Button, IconButton,
  Stepper, Step, StepLabel, Alert, FormControl, InputLabel,
  Select, MenuItem, Chip, RadioGroup, Radio, FormControlLabel,
  CircularProgress, LinearProgress, Checkbox, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getTiposNegocio, crearAliado, precheckAliado } from '../services/aliadosApi';
import { getConfiguracionTexto } from '../services/configuracionTextoApi';
import { getDepartamentos, getMunicipios } from '../services/ecolabApi';
import UbicacionMapaPicker from './UbicacionMapaPicker';
import VerificadorDocumento from './VerificadorDocumento';
import { generarContratoAliadoPDF } from '../utils/generarContratoAliadoPDF';
import ReactMarkdown from 'react-markdown';

// Componentes MUI-styled para react-markdown (T&C bien formateado)
const mdComponents = {
  h1: ({ node, ...p }) => <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a4d2e', mt: 2, mb: 1.5 }} {...p} />,
  h2: ({ node, ...p }) => <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mt: 2.5, mb: 1 }} {...p} />,
  h3: ({ node, ...p }) => <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }} {...p} />,
  p:  ({ node, ...p }) => <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.7, color: '#333' }} {...p} />,
  ul: ({ node, ...p }) => <Box component="ul" sx={{ pl: 3, mb: 1.5 }} {...p} />,
  ol: ({ node, ...p }) => <Box component="ol" sx={{ pl: 3, mb: 1.5 }} {...p} />,
  li: ({ node, ...p }) => <Box component="li" sx={{ mb: 0.5, fontSize: '0.9rem', lineHeight: 1.6, color: '#333' }} {...p} />,
  strong: ({ node, ...p }) => <Box component="strong" sx={{ fontWeight: 700, color: '#1a1a1a' }} {...p} />,
  em: ({ node, ...p }) => <Box component="em" sx={{ fontStyle: 'italic', color: '#555' }} {...p} />,
  code: ({ node, ...p }) => <Box component="code" sx={{
    bgcolor: '#f0f0f0', px: 0.6, py: 0.2, borderRadius: 0.5,
    fontFamily: 'monospace', fontSize: '0.85em',
  }} {...p} />,
  hr: () => <Box sx={{ my: 2, borderBottom: '1px solid', borderColor: 'divider' }} />,
};

const STEPS = ['Bienvenida', 'Negocio', 'Documentos', 'Ubicación', 'Descuento y envío'];

const FORM_INICIAL = {
  // Paso 2 — negocio
  tipoNegocioID:        '',
  razonSocial:          '',
  nombreComercial:      '',
  nit:                  '',
  representanteLegal:   '',
  cedulaRepresentante:  '',
  email:                '',
  telefono:             '',
  sitioWeb:             '',
  descripcion:          '',
  // Paso 3 — ubicación
  departamentoId:       '',
  municipioID:          '',
  direccionComercial:   '',
  latitud:              '',
  longitud:             '',
  // Paso 4 — descuento
  usaDescuentoRed:        true,
  descuentoPersonalizado: '',
};

export default function AliarseModal({ open, onClose, onSuccess }) {
  const [step, setStep]     = useState(0);
  const [form, setForm]     = useState(FORM_INICIAL);
  const [tipos, setTipos]   = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [munis, setMunis]   = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [okInfo, setOkInfo]     = useState(null); // { aliadoID, codigo }
  const [error, setError]       = useState(null);

  // ── T&C (paso 0) ──────────────────────────────────────────────────────
  const [tyc, setTyc]                     = useState(null);       // { clave, titulo, contenido, versionActual }
  const [tycLoading, setTycLoading]       = useState(false);
  const [tycError, setTycError]           = useState(null);
  const [tycModalAbierto, setTycModalAbierto] = useState(false);
  const [tycScrollLeido, setTycScrollLeido]   = useState(false);  // true cuando llega al final del modal
  const [tycAceptado, setTycAceptado]     = useState(false);      // checkbox
  const [aceptacion, setAceptacion]       = useState(null);       // { hash, fecha, pdfBlob }
  const tycContentRef                     = useRef(null);

  // ── Precheck de duplicidades y geografía ─────────────────────────────
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [precheckErrors, setPrecheckErrors]   = useState([]);   // bloquean
  const [precheckWarnings, setPrecheckWarnings] = useState([]); // informan
  const [warningsAbierto, setWarningsAbierto]   = useState(false);

  // ── Documentos (paso 2 nuevo) ────────────────────────────────────────
  // Cédula del representante legal + RUT del negocio. Ambos con OCR estricto.
  const [docs, setDocs] = useState({
    cedulaRep: { file: null, url: null },
    rut:       { file: null, url: null },
  });
  // Snapshot de los campos críticos al momento de validar cada documento.
  // Si el usuario retrocede al paso Negocio y cambia estos datos, el
  // documento correspondiente se invalida y hay que re-subirlo.
  const [snapshotCedulaRep, setSnapshotCedulaRep] = useState(null);
  const [snapshotRut, setSnapshotRut]             = useState(null);
  const [docsInvalidadosAviso, setDocsInvalidadosAviso] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm(FORM_INICIAL);
    setDocs({ cedulaRep: { file: null, url: null }, rut: { file: null, url: null } });
    setSnapshotCedulaRep(null);
    setSnapshotRut(null);
    setDocsInvalidadosAviso(false);
    setOkInfo(null);
    setError(null);
  }, [open]);

  // Catálogos
  useEffect(() => {
    if (!open) return;
    getTiposNegocio(false).then(setTipos).catch(() => setTipos([]));
    getDepartamentos(1045).then(setDeptos).catch(() => setDeptos([]));
  }, [open]);

  // Cargar T&C vigentes cuando se abre el modal
  useEffect(() => {
    if (!open) return;
    setTycLoading(true);
    setTycError(null);
    getConfiguracionTexto('TYC_ALIADO')
      .then(setTyc)
      .catch(e => setTycError(e.message || 'No se pudieron cargar los Términos y Condiciones.'))
      .finally(() => setTycLoading(false));
  }, [open]);

  // Detectar scroll al final en el modal de T&C para habilitar el checkbox
  const handleTycScroll = () => {
    const el = tycContentRef.current;
    if (!el) return;
    const alFinal = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (alFinal) setTycScrollLeido(true);
  };

  // Handler del checkbox: al marcar generamos el PDF + hash y guardamos
  const handleAceptarTyc = async (checked) => {
    if (!checked) {
      setTycAceptado(false);
      setAceptacion(null);
      return;
    }
    if (!tyc) return;
    try {
      const fecha = new Date();
      const { blob, hash } = await generarContratoAliadoPDF({
        tycTitulo:    tyc.titulo,
        tycContenido: tyc.contenido,
        tycVersion:   tyc.versionActual,
        aliado: {
          razonSocial:        form.razonSocial || '(pendiente de completar)',
          nit:                form.nit         || '(pendiente)',
          representanteLegal: form.representanteLegal || '(pendiente)',
          cedula:             form.cedulaRepresentante || '(pendiente)',
          email:              form.email      || '(pendiente)',
        },
        fechaAceptacion: fecha,
      });
      setAceptacion({ hash, fecha, pdfBlob: blob });
      setTycAceptado(true);
    } catch (e) {
      setTycError('No se pudo generar el contrato PDF: ' + (e.message || e));
      setTycAceptado(false);
      setAceptacion(null);
    }
  };

  useEffect(() => {
    if (!form.departamentoId) { setMunis([]); return; }
    getMunicipios(form.departamentoId).then(setMunis).catch(() => setMunis([]));
  }, [form.departamentoId]);

  // ── Antisupllantación: invalidar documentos si cambian datos del Negocio ──
  useEffect(() => {
    if (!docs.cedulaRep.url || !snapshotCedulaRep) return;
    const cambio =
      (form.representanteLegal || '').trim()  !== snapshotCedulaRep.representanteLegal ||
      (form.cedulaRepresentante || '').trim() !== snapshotCedulaRep.cedulaRepresentante;
    if (cambio) {
      setDocs(p => ({ ...p, cedulaRep: { file: null, url: null } }));
      setSnapshotCedulaRep(null);
      setDocsInvalidadosAviso(true);
    }
  }, [form.representanteLegal, form.cedulaRepresentante, docs.cedulaRep.url, snapshotCedulaRep]);

  useEffect(() => {
    if (!docs.rut.url || !snapshotRut) return;
    const cambio =
      (form.razonSocial || '').trim() !== snapshotRut.razonSocial ||
      (form.nit || '').trim()         !== snapshotRut.nit;
    if (cambio) {
      setDocs(p => ({ ...p, rut: { file: null, url: null } }));
      setSnapshotRut(null);
      setDocsInvalidadosAviso(true);
    }
  }, [form.razonSocial, form.nit, docs.rut.url, snapshotRut]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Handler estable para el mapa (evita re-renders que cambien la ref)
  const onCambioUbicacion = useCallback(({ lat, lng }) => {
    setForm(p => ({
      ...p,
      latitud:  lat != null ? String(lat) : '',
      longitud: lng != null ? String(lng) : '',
    }));
  }, []);

  // Validación por paso
  const puedeAvanzar = useMemo(() => {
    // Bienvenida: el usuario debe haber abierto y leído hasta el final el
    // modal de T&C. La aceptación formal (con hash sobre datos reales) se
    // pide al final del wizard, en el paso 5.
    if (step === 0) return tycScrollLeido;
    if (step === 1) return !!(form.tipoNegocioID && form.razonSocial.trim() && form.nit.trim()
      && form.representanteLegal.trim() && form.cedulaRepresentante.trim() && form.email.trim());  // Negocio
    if (step === 2) return !!(docs.cedulaRep.url && docs.rut.url);  // Documentos
    if (step === 3) return !!(form.municipioID && form.direccionComercial.trim());  // Ubicación
    if (step === 4) {                                       // Descuento y envío
      // Descuento válido
      let descuentoOk = false;
      if (form.usaDescuentoRed) descuentoOk = true;
      else {
        const n = parseFloat(form.descuentoPersonalizado);
        descuentoOk = !Number.isNaN(n) && n >= 0 && n <= 100;
      }
      // Aceptación formal con hash sobre datos reales
      return descuentoOk && tycAceptado && !!aceptacion?.hash;
    }
    return false;
  }, [step, form, docs, tycAceptado, aceptacion, tycScrollLeido]);

  // Al retroceder desde Documentos (step 2), invalidar ambos documentos y
  // forzar re-subida al volver — igual patrón que en afiliación de personas.
  const atras = () => {
    setStep(s => {
      if (s === 2) {
        setDocs({ cedulaRep: { file: null, url: null }, rut: { file: null, url: null } });
        setSnapshotCedulaRep(null);
        setSnapshotRut(null);
        setDocsInvalidadosAviso(true);
      }
      return Math.max(s - 1, 0);
    });
  };
  const ejecutarPrecheck = async () => {
    setPrecheckLoading(true);
    setPrecheckErrors([]);
    setPrecheckWarnings([]);
    try {
      const r = await precheckAliado({
        nit:                 form.nit,
        email:               form.email,
        razonSocial:         form.razonSocial,
        nombreComercial:     form.nombreComercial,
        cedulaRepresentante: form.cedulaRepresentante,
        municipioID:         form.municipioID ? parseInt(form.municipioID, 10) : null,
      });
      setPrecheckErrors(r.errors || []);
      setPrecheckWarnings(r.warnings || []);
      return r;
    } catch (e) {
      setPrecheckErrors([{ campo: 'general', mensaje: e.message || 'No se pudo verificar la información.' }]);
      return { puedeContinuar: false, errors: [], warnings: [] };
    } finally {
      setPrecheckLoading(false);
    }
  };

  const siguiente = async () => {
    // Al salir del paso Negocio (1) ejecutamos precheck de duplicidades.
    // Al salir del paso Ubicación (3) volvemos a chequear porque puede haber
    // cambiado el municipio y con eso la coherencia geográfica.
    if (step === 1 || step === 3) {
      const r = await ejecutarPrecheck();
      if (!r.puedeContinuar) return;  // errors bloquean, no avanza
      if ((r.warnings || []).length > 0) {
        setWarningsAbierto(true);     // muestra dialog, el usuario decide
        return;
      }
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const confirmarWarningsYAvanzar = () => {
    setWarningsAbierto(false);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const enviar = async () => {
    setError(null);
    setEnviando(true);
    try {
      const dto = {
        tipoNegocioID:          parseInt(form.tipoNegocioID, 10),
        razonSocial:            form.razonSocial.trim(),
        nombreComercial:        form.nombreComercial.trim() || null,
        nit:                    form.nit.trim(),
        representanteLegal:     form.representanteLegal.trim(),
        cedulaRepresentante:    form.cedulaRepresentante.trim(),
        email:                  form.email.trim().toLowerCase(),
        telefono:               form.telefono.trim() || null,
        sitioWeb:               form.sitioWeb.trim() || null,
        descripcion:            form.descripcion.trim() || null,
        municipioID:            parseInt(form.municipioID, 10),
        direccionComercial:     form.direccionComercial.trim(),
        latitud:                form.latitud  ? parseFloat(form.latitud)  : null,
        longitud:               form.longitud ? parseFloat(form.longitud) : null,
        usaDescuentoRed:        form.usaDescuentoRed,
        descuentoPersonalizado: form.usaDescuentoRed ? null : parseFloat(form.descuentoPersonalizado),
        // Documentos verificados en el paso 2 (URLs gs:// firmadas por backend)
        urlCedulaRepresentante: docs.cedulaRep.url || null,
        urlRut:                 docs.rut.url       || null,
        // Aceptación de T&C
        terminosAceptados:    true,
        tyCVersionAceptada:   tyc?.versionActual,
        hashContrato:         aceptacion?.hash,
        terminosAceptadosEn:  aceptacion?.fecha?.toISOString(),
      };
      const r = await crearAliado(dto);
      setOkInfo({ aliadoID: r.aliadoID, codigo: r.codigo });
      if (onSuccess) onSuccess(r);
    } catch (e) {
      setError(e?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
        color: '#fff', display:'flex', alignItems:'center', justifyContent:'space-between', py: 1.5,
      }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <StorefrontOutlinedIcon />
          <Box>
            <Typography sx={{ fontWeight:700, lineHeight:1.2 }}>Aliarse a la red</Typography>
            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.8)' }}>
              Registro de establecimiento comercial bajo FENAGRO APL
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color:'#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: '32px !important' }}>
        {!okInfo && (
          <Stepper activeStep={step} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        )}

        {enviando && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ── OK ── */}
        {okInfo && (
          <Box sx={{ textAlign:'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color:'#2e7d32', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>¡Solicitud enviada!</Typography>
            <Typography sx={{ color:'#555', mb: 2 }}>
              Tu código de aliado es <strong>{okInfo.codigo}</strong>.
              Un auditor revisará los datos y te contactaremos por email para los siguientes pasos
              (subir documentos y realizar el pago de afiliación).
            </Typography>
            <Alert severity="info" sx={{ textAlign:'left' }}>
              Guarda este código: te servirá para consultar el estado de tu solicitud.
            </Alert>
          </Box>
        )}

        {/* ── Paso 0 — Bienvenida ── */}
        {!okInfo && step === 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Sé aliado comercial de la Red
            </Typography>
            <Typography sx={{ color: '#444', mb: 2 }}>
              Este proceso es para <strong>establecimientos comerciales</strong> (tiendas, panaderías,
              ferreterías, droguerías, etc.) que quieren integrarse a la red bajo FENAGRO APL para:
            </Typography>
            <Box component="ul" sx={{ color: '#444', pl: 3, mb: 2 }}>
              <li>Vender en el <strong>marketplace en línea</strong> de la red.</li>
              <li>Vender a través del <strong>POS presencial</strong> en su propio establecimiento.</li>
              <li>Aceptar <strong>códigos de canje</strong> presenciales de futuros asociados.</li>
              <li>Aumentar sus ventas con exposición dentro de la red.</li>
              <li>Puertas abiertas a los beneficios plenos si el dueño luego se afilia como asociado.</li>
            </Box>
            <Alert severity="warning" sx={{ mb: 2, fontSize: '0.85rem' }}>
              El descuento con el que venderá en el marketplace se acuerda <strong>previamente</strong> con
              el gestor comercial de la federación. Antes de continuar asegúrese de tener claro ese
              porcentaje.
            </Alert>

            {/* Enlace informativo a T&C (la aceptación formal es en el paso 5) */}
            <Box sx={{
              border: '1.5px solid', borderColor: tycScrollLeido ? 'success.main' : '#c8e6c9',
              borderRadius: 2, p: 2, mb: 1, bgcolor: tycScrollLeido ? '#f0fdf0' : '#fafafa',
            }}>
              {tycLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Cargando Términos y Condiciones…</Typography>
                </Box>
              )}
              {tycError && !tycLoading && (
                <Alert severity="error">{tycError}</Alert>
              )}
              {tyc && !tycLoading && !tycError && (
                <>
                  <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                    Antes de continuar, lea los{' '}
                    <Link component="button" type="button"
                          onClick={() => setTycModalAbierto(true)}
                          sx={{ fontWeight: 700, verticalAlign: 'baseline' }}>
                      Términos y Condiciones del Aliado Comercial
                    </Link>
                    {' '}(versión {tyc.versionActual}).
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Al final del proceso deberá aceptarlos formalmente para completar la solicitud.
                  </Typography>
                  {tycScrollLeido && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.8rem' }} icon={<CheckCircleIcon />}>
                      Documento leído completo. Puede continuar con la inscripción.
                    </Alert>
                  )}
                </>
              )}
            </Box>

            <Typography variant="caption" sx={{ color: '#888' }}>
              Al continuar, se le pedirán los datos legales y de ubicación de su negocio. La solicitud
              queda pendiente de aprobación por un auditor.
            </Typography>
          </Box>
        )}

        {/* ── Paso 1 — Negocio ── */}
        {!okInfo && step === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Chip icon={<StorefrontOutlinedIcon />} label="Se afiliará bajo: FENAGRO APL"
                    sx={{ bgcolor:'#e8f5e9', color:'#1a4d2e', fontWeight:600 }} />
            </Grid>
            {precheckErrors.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontSize: '0.88rem' }} onClose={() => setPrecheckErrors([])}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    No podemos continuar con la información actual:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {precheckErrors.map((e, i) => (
                      <li key={i}><Typography variant="body2">{e.mensaje}</Typography></li>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Tipo de negocio *</InputLabel>
                <Select value={form.tipoNegocioID} label="Tipo de negocio *"
                        onChange={e => set('tipoNegocioID', e.target.value)}>
                  <MenuItem value=""><em>Seleccione…</em></MenuItem>
                  {tipos.map(t => (
                    <MenuItem key={t.tipoNegocioID} value={t.tipoNegocioID}>{t.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="NIT *" value={form.nit}
                onChange={e => set('nit', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth size="small" label="Razón social *" value={form.razonSocial}
                onChange={e => set('razonSocial', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth size="small" label="Nombre comercial" value={form.nombreComercial}
                onChange={e => set('nombreComercial', e.target.value)}
                helperText="Como se conoce al negocio, si es distinto." />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth size="small" label="Representante legal *" value={form.representanteLegal}
                onChange={e => set('representanteLegal', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth size="small" label="Cédula del representante *"
                value={form.cedulaRepresentante} onChange={e => set('cedulaRepresentante', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Email *" value={form.email}
                onChange={e => set('email', e.target.value)} type="email" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Teléfono" value={form.telefono}
                onChange={e => set('telefono', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Sitio web" value={form.sitioWeb}
                onChange={e => set('sitioWeb', e.target.value)} placeholder="https://…" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Descripción del negocio"
                value={form.descripcion} onChange={e => set('descripcion', e.target.value)}
                helperText="Este texto puede mostrarse en el marketplace después de la aprobación." />
            </Grid>
          </Grid>
        )}

        {/* ── Paso 2 — Ubicación ── */}
        {/* ── Paso 2 — Documentos ── */}
        {!okInfo && step === 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Documentos del establecimiento</Typography>
            <Typography variant="body2" sx={{ color:'#666', mb: 2 }}>
              Antes de continuar debemos verificar que los datos ingresados coinciden
              con los documentos oficiales del negocio y del representante legal.
            </Typography>

            {docsInvalidadosAviso && (
              <Alert
                severity="warning"
                sx={{ mb: 2, fontSize: '0.85rem', fontWeight: 500 }}
                onClose={() => setDocsInvalidadosAviso(false)}
              >
                🔒 <strong>Por seguridad, debes volver a subir los documentos.</strong>
                {' '}Cada vez que regresas a este paso o modificas los datos del negocio
                se requiere una nueva verificación.
              </Alert>
            )}

            {/* Cédula del representante */}
            <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                Cédula del representante legal (ambas caras)
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                📋 Suba la cédula escaneada por <strong>ambas caras (frente y reverso) en un solo archivo PDF</strong> de <strong>{form.representanteLegal || '—'}</strong>
                {' '}(CC {form.cedulaRepresentante || '—'}). Los datos deben coincidir exactamente.
              </Alert>
              <VerificadorDocumento
                titulo=""
                descripcion="Formato preferido: PDF con ambas caras en el mismo archivo. También se aceptan JPG y PNG. Máximo 10MB."
                cedula={form.cedulaRepresentante}
                nombres={form.representanteLegal}
                apellidos=""
                tipoDocumento="cedula"
                onValido={(file, resultado) => {
                  if (resultado?.urlDocumento) {
                    setDocs(p => ({ ...p, cedulaRep: { file, url: resultado.urlDocumento } }));
                    setSnapshotCedulaRep({
                      representanteLegal:  (form.representanteLegal || '').trim(),
                      cedulaRepresentante: (form.cedulaRepresentante || '').trim(),
                    });
                    setDocsInvalidadosAviso(false);
                  }
                }}
                onInvalido={() => {
                  setDocs(p => ({ ...p, cedulaRep: { file: null, url: null } }));
                  setSnapshotCedulaRep(null);
                }}
              />
            </Box>

            {/* RUT del negocio */}
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                RUT del negocio
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
                Sube el RUT (o certificado de existencia) de <strong>{form.razonSocial || '—'}</strong>
                {' '}(NIT {form.nit || '—'}). La razón social y el NIT deben aparecer en el documento.
              </Typography>
              <VerificadorDocumento
                titulo=""
                descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10MB."
                cedula={form.nit}
                nombres={form.razonSocial}
                apellidos=""
                tipoDocumento="rut"
                onValido={(file, resultado) => {
                  if (resultado?.urlDocumento) {
                    setDocs(p => ({ ...p, rut: { file, url: resultado.urlDocumento } }));
                    setSnapshotRut({
                      razonSocial: (form.razonSocial || '').trim(),
                      nit:         (form.nit || '').trim(),
                    });
                    setDocsInvalidadosAviso(false);
                  }
                }}
                onInvalido={() => {
                  setDocs(p => ({ ...p, rut: { file: null, url: null } }));
                  setSnapshotRut(null);
                }}
              />
            </Box>
          </Box>
        )}

        {/* ── Paso 3 — Ubicación ── */}
        {!okInfo && step === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ fontSize:'0.85rem' }}>
                La ubicación es fundamental para el marketplace y para conectar con clientes cercanos.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Departamento *</InputLabel>
                <Select value={form.departamentoId} label="Departamento *"
                  onChange={e => setForm(p => ({ ...p, departamentoId: e.target.value, municipioID: '' }))}>
                  <MenuItem value=""><em>Seleccione…</em></MenuItem>
                  {deptos.map(d => (
                    <MenuItem key={d.departamentoID} value={d.departamentoID}>{d.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth disabled={!form.departamentoId}>
                <InputLabel>Ciudad / Municipio *</InputLabel>
                <Select value={form.municipioID} label="Ciudad / Municipio *"
                  onChange={e => set('municipioID', e.target.value)}>
                  <MenuItem value=""><em>Seleccione…</em></MenuItem>
                  {munis.map(m => (
                    <MenuItem key={m.municipioID} value={m.municipioID}>{m.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Dirección comercial *"
                value={form.direccionComercial} onChange={e => set('direccionComercial', e.target.value)}
                placeholder="Ej: Calle 45 # 12-34, Barrio Centro" />
            </Grid>
            <Grid item xs={12}>
              <UbicacionMapaPicker
                latitud={form.latitud === '' ? null : parseFloat(form.latitud)}
                longitud={form.longitud === '' ? null : parseFloat(form.longitud)}
                onCambio={onCambioUbicacion}
                direccionParaGeocode={form.direccionComercial}
                ciudadParaGeocode={munis.find(m => m.municipioID === form.municipioID)?.nombre || ''}
                departamentoParaGeocode={deptos.find(d => d.departamentoID === form.departamentoId)?.nombre || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Latitud"
                value={form.latitud}
                InputProps={{ readOnly: true }}
                variant="filled"
                helperText="Se completa al ubicar el pin en el mapa."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Longitud"
                value={form.longitud}
                InputProps={{ readOnly: true }}
                variant="filled"
                helperText="Solo informativo."
              />
            </Grid>
          </Grid>
        )}

        {/* ── Paso 4 — Descuento y envío ── */}
        {!okInfo && step === 4 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Descuento en marketplace</Typography>
            <Typography variant="body2" sx={{ color:'#666', mb: 2 }}>
              ¿Cómo aplicará el descuento a los productos que venda en el marketplace?
            </Typography>
            <RadioGroup value={form.usaDescuentoRed ? 'red' : 'personal'}
              onChange={e => set('usaDescuentoRed', e.target.value === 'red')}>
              <FormControlLabel value="red" control={<Radio />}
                label={<Box>
                  <Typography sx={{ fontWeight: 600 }}>Usar descuento de la red</Typography>
                  <Typography variant="caption" sx={{ color:'#666' }}>
                    El porcentaje lo administra la red desde el marketplace y puede variar.
                  </Typography>
                </Box>} />
              <FormControlLabel value="personal" control={<Radio />}
                label={<Box>
                  <Typography sx={{ fontWeight: 600 }}>Personalizar mi descuento</Typography>
                  <Typography variant="caption" sx={{ color:'#666' }}>
                    Fijo un porcentaje específico (previamente acordado con el gestor comercial).
                  </Typography>
                </Box>} />
            </RadioGroup>
            {!form.usaDescuentoRed && (
              <TextField size="small" label="Porcentaje de descuento (%)"
                value={form.descuentoPersonalizado} onChange={e => set('descuentoPersonalizado', e.target.value)}
                type="number" inputProps={{ min:0, max:100, step:'0.5' }}
                sx={{ mt: 2, maxWidth: 260 }} />
            )}

            <Box sx={{ mt: 4, p: 2, bgcolor:'#f5f7f5', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Resumen</Typography>
              <Grid container spacing={1} sx={{ fontSize:'0.85rem' }}>
                <Grid item xs={5} sx={{ color:'#666' }}>Razón social:</Grid>
                <Grid item xs={7}>{form.razonSocial || '—'}</Grid>
                <Grid item xs={5} sx={{ color:'#666' }}>NIT:</Grid>
                <Grid item xs={7}>{form.nit || '—'}</Grid>
                <Grid item xs={5} sx={{ color:'#666' }}>Representante:</Grid>
                <Grid item xs={7}>{form.representanteLegal || '—'}</Grid>
                <Grid item xs={5} sx={{ color:'#666' }}>Email:</Grid>
                <Grid item xs={7}>{form.email || '—'}</Grid>
                <Grid item xs={5} sx={{ color:'#666' }}>Ciudad:</Grid>
                <Grid item xs={7}>
                  {munis.find(m => m.municipioID === form.municipioID)?.nombre || '—'}
                </Grid>
                <Grid item xs={5} sx={{ color:'#666' }}>Descuento:</Grid>
                <Grid item xs={7}>
                  {form.usaDescuentoRed ? 'Descuento de la red' : `${form.descuentoPersonalizado || 0}% personalizado`}
                </Grid>
              </Grid>
            </Box>

            {/* Aceptación formal de T&C — con datos reales del wizard */}
            {tyc && (
              <Box sx={{
                mt: 3, p: 2,
                border: '1.5px solid', borderColor: tycAceptado ? 'success.main' : '#c8e6c9',
                borderRadius: 2, bgcolor: tycAceptado ? '#f0fdf0' : '#fafafa',
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1a4d2e' }}>
                  Aceptación de Términos y Condiciones
                </Typography>
                <Typography variant="body2" sx={{ color: '#444', mb: 1 }}>
                  Puede{' '}
                  <Link component="button" type="button"
                        onClick={() => setTycModalAbierto(true)}
                        sx={{ fontWeight: 700, verticalAlign: 'baseline' }}>
                    releer los Términos y Condiciones (versión {tyc.versionActual})
                  </Link>{' '}
                  antes de aceptar.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tycAceptado}
                      onChange={e => handleAceptarTyc(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      He leído y acepto los Términos y Condiciones (v{tyc.versionActual}).
                      Autorizo la firma electrónica del contrato con los datos aquí registrados.
                    </Typography>
                  }
                />
                {tycAceptado && aceptacion && (
                  <Alert severity="success" sx={{ mt: 1.5, fontSize: '0.8rem' }} icon={<CheckCircleIcon />}>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Aceptación registrada el {aceptacion.fecha.toLocaleString('es-CO')}.
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Contrato PDF generado con hash de trazabilidad:{' '}
                      <code style={{ fontSize: '0.75rem' }}>{aceptacion.hash.substring(0, 24)}…</code>
                    </Typography>
                    <Link component="button" type="button"
                          onClick={() => window.open(URL.createObjectURL(aceptacion.pdfBlob), '_blank')}
                          sx={{ fontSize: '0.8rem', mt: 0.5, display: 'inline-block' }}>
                      Ver contrato generado (PDF)
                    </Link>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {okInfo ? (
          <Button onClick={onClose} variant="contained">Cerrar</Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit" disabled={enviando}>Cancelar</Button>
            <Box sx={{ flex: 1 }} />
            {step > 0 && (
              <Button onClick={atras} disabled={enviando}>Atrás</Button>
            )}
            {step < STEPS.length - 1 && (
              <Button variant="contained" onClick={siguiente}
                disabled={!puedeAvanzar || precheckLoading}
                startIcon={precheckLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor:'#2e7d32', '&:hover': { bgcolor:'#1a4d2e' } }}>
                {precheckLoading ? 'Verificando…' : 'Siguiente'}
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button variant="contained" onClick={enviar}
                disabled={!puedeAvanzar || enviando}
                startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor:'#2e7d32', '&:hover': { bgcolor:'#1a4d2e' } }}>
                Enviar solicitud
              </Button>
            )}
          </>
        )}
      </DialogActions>

      {/* ── Modal de Términos y Condiciones ── */}
      <Dialog open={tycModalAbierto} onClose={() => setTycModalAbierto(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
          color: '#fff', display:'flex', alignItems:'center', justifyContent:'space-between', py: 1.5,
        }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <DescriptionOutlinedIcon />
            <Box>
              <Typography sx={{ fontWeight:700, lineHeight:1.2 }}>{tyc?.titulo || 'Términos y Condiciones'}</Typography>
              <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.85)' }}>
                Versión {tyc?.versionActual || '—'} · Desplácese hasta el final para poder aceptar
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setTycModalAbierto(false)} sx={{ color:'#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers ref={tycContentRef} onScroll={handleTycScroll}
          sx={{ py: 2, px: 3 }}>
          {tyc && (
            <Box sx={{ maxWidth: '100%' }}>
              <ReactMarkdown components={mdComponents}>
                {tyc.contenido}
              </ReactMarkdown>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: tycScrollLeido ? 'success.main' : 'text.secondary' }}>
            {tycScrollLeido ? '✓ Documento leído completo' : 'Desplácese hasta el final para habilitar la aceptación'}
          </Typography>
          <Button variant="contained" onClick={() => setTycModalAbierto(false)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1a4d2e' } }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog de advertencias del precheck ── */}
      <Dialog open={warningsAbierto} onClose={() => setWarningsAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Verifiquemos su información
          <IconButton size="small" onClick={() => setWarningsAbierto(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: '#444' }}>
            Detectamos lo siguiente sobre los datos que ingresó. No bloquea la inscripción,
            pero es importante que lo tenga en cuenta:
          </Typography>
          {precheckWarnings.map((w, i) => (
            <Alert key={i} severity="warning" sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{w.mensaje}</Typography>
              {w.cooperativa && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {w.cooperativa.nombre} ({w.cooperativa.siglas}) · {w.cooperativa.municipio || w.cooperativa.departamento || ''}
                </Typography>
              )}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningsAbierto(false)}>Revisar mis datos</Button>
          <Button variant="contained" onClick={confirmarWarningsYAvanzar}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1a4d2e' } }}>
            Entendido, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
